/**
 * uiHandlers.ts — Handler map for UIActions.
 * One handler per action type, extracted from the switch in uiReducer.
 */
import type { UIState, UIAction } from "./types";
import {
  loadScheduledRetreat,
  saveScheduledRetreat,
  clearScheduledRetreat,
  loadLastUsedSchedule,
  saveLastUsedSchedule,
  saveWakeOffset,
} from "../state/schedulingState";
import { oneDayRetreat, INTENTIONS, INTENTION_CATEGORIES } from "@retreats/content";
import type { IntentTag } from "@retreats/content";
import { STORAGE_KEYS, UI_ACTION_TYPES, INTENTION_DISPLAY_LIMITS } from "../constants";
import { createInitialUIState } from "./initialUIState";
import {
  getSettingsSnapshot,
  getAudioReflectionEnabled,
} from "../state/settingsState";
import { createChildLogger } from "@retreat/shared";
import { track, getCurrentWave } from "../analytics/posthog";
import { safeLocalStorageSetItem, safeLocalStorageRemoveItem, safeLocalStorageGetItem } from "../utils/storageUtils";

const log = createChildLogger("uiHandlers");

function deriveIntentData(record: Extract<UIAction, { type: "SET_INTENTION" }>["record"]): { intentLabels: string[]; primaryIntentTag: IntentTag | null } {
  if (record.kind !== 'set') return { intentLabels: [], primaryIntentTag: null };

  const labels: string[] = record.intentionIds
    .map(id => {
      const intention = INTENTIONS.find(i => i.id === id);
      if (!intention) {
        log.trace('Unknown intentionId: %s', id);
        return null;
      }
      return intention.label;
    })
    .filter((l): l is string => l !== null);

  if (record.customText?.trim()) {
    let custom = record.customText.trim();
    if (custom.length > INTENTION_DISPLAY_LIMITS.MAX_CUSTOM_TEXT_CHARS) {
      custom = custom.slice(0, INTENTION_DISPLAY_LIMITS.MAX_CUSTOM_TEXT_CHARS) + INTENTION_DISPLAY_LIMITS.LABEL_TRUNCATION;
    }
    labels.push(custom);
  }

  const capped = labels.slice(0, INTENTION_DISPLAY_LIMITS.MAX_LABEL_COUNT);

  const firstIntention = INTENTIONS.find(i => i.id === record.intentionIds[0]);
  const primaryCategoryId = firstIntention?.category ?? null;
  const primaryIntentTag: IntentTag | null = primaryCategoryId !== null &&
    INTENTION_CATEGORIES.some(c => c.id === primaryCategoryId)
      ? (primaryCategoryId as IntentTag)
      : null;

  return { intentLabels: capped, primaryIntentTag };
}

type H<A extends UIAction = UIAction> = (state: UIState, action: A) => UIState;

// Clears all peer-level navigation flags before activating a new primary destination.
// Every "open" action spreads this so deriveScreen's priority order can never silently
// swallow a navigation action because an older flag was left set.
const NAV_RESET = {
  showHistory:       false,
  showSettings:      false,
  libraryFlow:       null as UIState["libraryFlow"],
  viewingSchedule:   null as UIState["viewingSchedule"],
  viewingTemplateId: null as UIState["viewingTemplateId"],
  viewingOrigin:     "preset" as UIState["viewingOrigin"],
  viewingBaseScheduleId: null as UIState["viewingBaseScheduleId"],
  bellFlow:          null as UIState["bellFlow"],
  previewingBellId:  null as UIState["previewingBellId"],
};

export const uiHandlers: { [K in UIAction["type"]]?: H<Extract<UIAction, { type: K }>> } = {
  SHOW_END_CONFIRM: (s) => (s.showCompletion ? s : {
    ...s,
    showEndConfirm: true,
    libraryFlow: null,
    viewingSchedule: null,
    isReturning: false,
    otherTabActive: false,
    otherTabRetreatName: null,
  }),
  CANCEL_END: (s) => ({ ...s, showEndConfirm: false }),
  SHOW_COMPLETION: (s, a) => {
    track("retreat_completed", { retreat_id: s.selectedSchedule?.id ?? 'unknown', wave: getCurrentWave() });
    clearScheduledRetreat();
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENT_LABELS);
    safeLocalStorageRemoveItem(STORAGE_KEYS.PRIMARY_INTENT_TAG);
    return {
      ...s,
      showCompletion: true,
      showEndConfirm: false,
      showEndIntention: false,
      hasScheduledRetreat: false,
      schedulingFlow: null,
      // Keep selectedSchedule so completion screen shows the correct retreat name.
      // It's cleared when the user dismisses and starts fresh (OPEN_SELECTION etc).
      scheduledAt: null,
      completionElapsedSeconds: a.elapsedSeconds ?? s.completionElapsedSeconds,
      isReturning: false,
      otherTabRetreatName: null,
      intentLabels: [],
      primaryIntentTag: null,
    };
  },
  DISMISS_COMPLETION: (s) => ({ ...s, showCompletion: false }),
  PERMISSIONS_GRANTED: (s) => {
    safeLocalStorageSetItem(STORAGE_KEYS.PERMISSIONS_GRANTED, "true");
    return { ...s, isFirstLaunch: false };
  },
  PERMISSIONS_DISMISSED: (s) => ({ ...s, isFirstLaunch: false }),
  PREPARATION_SEEN: (s) => {
    safeLocalStorageSetItem(STORAGE_KEYS.PREPARATION_SEEN, "true");
    return { ...s, preparationSeen: true };
  },
  BOOT_COMPLETE: (s) => ({ ...s, isBooting: false }),
  RESET: (_s) => createInitialUIState(),
  ONBOARDING_COMPLETE: (s) => {
    safeLocalStorageSetItem(STORAGE_KEYS.ONBOARDING_SEEN, '1');
    return { ...s, onboardingSeen: true };
  },
  OPEN_FEEDBACK: (s) => {
    track("feedback_opened", { wave: getCurrentWave() });
    return { ...s, ...NAV_RESET, showFeedback: true };
  },
  CLOSE_FEEDBACK: (s) => ({ ...s, showFeedback: false }),
  OPEN_ADMIN: (s) => ({ ...s, ...NAV_RESET, showAdmin: true }),
  CLOSE_ADMIN: (s) => ({ ...s, showAdmin: false }),
  OPEN_WHATS_NEW:  (s) => ({ ...s, ...NAV_RESET, showWhatsNew: true }),
  CLOSE_WHATS_NEW: (s) => ({ ...s, showWhatsNew: false }),
  OPEN_SCHEDULE_EDITOR: (s, a) => ({
    ...s, ...NAV_RESET, showScheduleEditor: true,
    scheduleEditorMode: a.editorMode,
    scheduleEditorSchedule: a.editorMode === "new" ? null : a.schedule,
    scheduleEditorBaseId: a.editorMode === "edit" ? a.baseScheduleId : null,
    scheduleEditorSaveError: false,
  }),
  CLOSE_SCHEDULE_EDITOR: (s) => ({
    ...s, showScheduleEditor: false, scheduleEditorSchedule: null,
    scheduleEditorBaseId: null, scheduleEditorSaveError: false,
    libraryFlow: "library" as const, libraryInitialTradition: "mine",
  }),
  SCHEDULE_EDITOR_SAVED: (s) => ({
    ...s, showScheduleEditor: false, scheduleEditorSchedule: null, scheduleEditorBaseId: null,
    scheduleEditorSaveError: false,
    libraryFlow: "library" as const, libraryInitialTradition: "mine",
    userScheduleVersion: s.userScheduleVersion + 1,
  }),
  // Editor stays open and edits are kept — only the error flag flips, so the user can retry.
  SCHEDULE_EDITOR_SAVE_FAILED: (s) => ({ ...s, scheduleEditorSaveError: true }),
  USER_SCHEDULE_DELETED: (s) => ({ ...s, userScheduleVersion: s.userScheduleVersion + 1 }),
  OPEN_SELECTION: (s) => ({
    ...s,
    schedulingFlow: "selection",
    selectedSchedule: null,
  }),
  SELECT_RETREAT: (s, a) => {
    saveLastUsedSchedule(a.schedule);
    return {
      ...s,
      schedulingFlow: "scheduling",
      selectedSchedule: a.schedule,
    };
  },
  CONFIRM_SCHEDULE: (s, a) => {
    // Use the wizard-generated schedule when provided, otherwise fall back to selectedSchedule
    const scheduleToSave = a.generatedSchedule ?? s.selectedSchedule;
    if (scheduleToSave) {
      saveScheduledRetreat(scheduleToSave, a.scheduledAt);
      if (a.wakeOffset) saveWakeOffset(a.wakeOffset);
    }
    return {
      ...s,
      schedulingFlow: null,
      scheduledAt: a.scheduledAt,
      hasScheduledRetreat: true,
      selectedSchedule: scheduleToSave ?? s.selectedSchedule,
      pendingScheduledAt: null,
      pendingWakeOffset: null,
      // Clear any stale warning from a previous attempt — PushAlarmService.register()
      // reports the real result for this attempt via PUSH_ALARM_REGISTRATION_RESULT.
      pushAlarmUnconfirmed: false,
    };
  },
  CANCEL_SCHEDULING: (s) => {
    const returnSchedule = s.schedulingReturnSchedule;
    return {
      ...s,
      schedulingFlow: null,
      schedulingReturnSchedule: null,
      pendingScheduledAt: null,
      pendingWakeOffset: null,
      selectedSchedule: s.selectedSchedule ?? loadLastUsedSchedule() ?? oneDayRetreat,
      // If we entered scheduling from RetreatDetailScreen, go back to it
      ...(returnSchedule ? {
        libraryFlow: "retreat-detail" as const,
        viewingSchedule: returnSchedule,
      } : {}),
    };
  },
  CANCEL_SCHEDULED_RETREAT: (s) => {
    clearScheduledRetreat();
    return {
      ...s,
      hasScheduledRetreat: false,
      isArmed: false,
      isMissed: false,
      selectedSchedule: null,
      scheduledAt: null,
      pushAlarmUnconfirmed: false,
    };
  },
  LOAD_SCHEDULED_RETREAT: (s) => {
    const scheduled = loadScheduledRetreat();
    if (!scheduled) {
      return {
        ...s,
        hasScheduledRetreat: false,
        selectedSchedule: null,
        scheduledAt: null,
      };
    }
    return {
      ...s,
      hasScheduledRetreat: true,
      selectedSchedule: scheduled.schedule,
      scheduledAt: scheduled.scheduledAt,
    };
  },
  BEGIN_SCHEDULED_RETREAT: (s) => {
    clearScheduledRetreat();
    return {
      ...s,
      hasScheduledRetreat: false,
      isArmed: false,
      schedulingFlow: null,
      showWakeUpContainer: false,
      showWakeRitual: true,
    };
  },
  ENTER_ARMED: (s) => ({ ...s, isArmed: true }),
  ENTER_MISSED: (s) => {
    clearScheduledRetreat();
    return {
      ...s,
      isMissed: true,
      hasScheduledRetreat: false,
      isArmed: false,
      selectedSchedule: null,
      scheduledAt: null,
    };
  },
  SHOW_WAKE_UP_CONTAINER: (s) => ({ ...s, showWakeUpContainer: true }),
  DISMISS_WAKE_UP_CONTAINER: (s) => ({ ...s, showWakeUpContainer: false }),
  NAVIGATE_TO_TRUST: (s) => ({ ...s, showTrust: true }),
  CLOSE_TRUST: (s) => ({ ...s, showTrust: false }),
  OPEN_TEACHING: (s) => ({ ...s, showTeaching: true }),
  CLOSE_TEACHING: (s) => ({ ...s, showTeaching: false }),
  SHOW_WAKE_RITUAL: (s) => ({ ...s, showWakeRitual: true }),
  DISMISS_WAKE_RITUAL: (s) => ({ ...s, showWakeRitual: false, showWakeUpContainer: true }),
  OPEN_LIBRARY: (s, a) => {
    const isFirst = !safeLocalStorageGetItem(STORAGE_KEYS.LIBRARY_SEEN);
    if (isFirst) safeLocalStorageSetItem(STORAGE_KEYS.LIBRARY_SEEN, '1');
    return { ...s, ...NAV_RESET, libraryFlow: "library", firstLibraryOpen: isFirst, libraryInitialTradition: a.tradition ?? null };
  },
  VIEW_RETREAT_DETAIL: (s, a) => {
    track("retreat_opened", { retreat_id: a.schedule.id, wave: getCurrentWave() });
    return {
      ...s,
      libraryFlow: "retreat-detail",
      viewingSchedule: a.schedule,
      viewingTemplateId: a.templateId ?? null,
      viewingOrigin: a.origin,
      viewingBaseScheduleId: a.baseScheduleId,
    };
  },
  GO_TO_WIZARD: (s, a) => ({
    ...s,
    schedulingFlow: "wizard",
    pendingScheduledAt: a.scheduledAt,
    pendingWakeOffset: a.wakeOffset ?? null,
  }),
  CLOSE_LIBRARY: (s) => ({ ...s, libraryFlow: null, viewingSchedule: null, firstLibraryOpen: false, libraryInitialTradition: null }),
  START_NOW: (s, a) => {
    saveLastUsedSchedule(a.selectedSchedule);
    return {
      ...s,
      libraryFlow: null,
      viewingSchedule: null,
      selectedSchedule: a.selectedSchedule,
    };
  },
  SCHEDULE_FROM_DETAIL: (s) => ({
    ...s,
    libraryFlow: null,
    viewingSchedule: null,
    schedulingFlow: "scheduling",
    selectedSchedule: s.viewingSchedule,
    schedulingReturnSchedule: s.viewingSchedule,
  }),
  SHOW_INSTALL_BRIDGE: (s) => ({ ...s, installFlow: "install-bridge" }),
  SHOW_INSTALL_PROMPT: (s) => ({ ...s, installFlow: "install-prompt" }),
  DISMISS_INSTALL: (s) => ({ ...s, installFlow: null }),
  OPEN_HISTORY: (s) => {
    track("history_viewed", { wave: getCurrentWave() });
    return { ...s, ...NAV_RESET, showHistory: true };
  },
  CLOSE_HISTORY: (s) => ({ ...s, showHistory: false }),
  OPEN_SETTINGS: (s) => ({ ...s, ...NAV_RESET, showSettings: true }),
  CLOSE_SETTINGS: (s) => ({
    ...s,
    showSettings: false,
    bellFlow: null,
    previewingBellId: null,
  }),
  OPEN_BELL_LIBRARY: (s) => ({ ...s, bellFlow: "bell-library", previewingBellId: null }),
  PREVIEW_BELL: (s, a) => ({
    ...s,
    bellFlow: "bell-preview",
    previewingBellId: a.bellId,
  }),
  CLOSE_BELL_PREVIEW: (s) => ({ ...s, bellFlow: "bell-library", previewingBellId: null }),
  CLOSE_BELL_LIBRARY: (s) => ({ ...s, bellFlow: null, previewingBellId: null }),
  SHOW_REFLECTION: (s) => ({ ...s, showReflection: true }),
  DISMISS_REFLECTION: (s) => ({ ...s, showReflection: false }),
  BEGIN_RETREAT: (s, a) => {
    const pendingAction = a.pendingAction ?? 'now';
    const retreatId = a.selectedSchedule?.id ?? s.selectedSchedule?.id ?? 'unknown';
    track("retreat_started", { retreat_id: retreatId, wave: getCurrentWave() });
    safeLocalStorageSetItem(STORAGE_KEYS.INTENTION_PENDING, 'true');
    safeLocalStorageSetItem(STORAGE_KEYS.INTENTION_PENDING_ACTION, pendingAction);
    if (a.selectedSchedule) saveLastUsedSchedule(a.selectedSchedule);
    return {
      ...s,
      intentionAction: pendingAction,
      selectedSchedule: a.selectedSchedule ?? s.selectedSchedule,
      libraryFlow: null,
      viewingSchedule: null,
    };
  },
  SET_INTENTION: (s, a) => {
    const { intentLabels, primaryIntentTag } = deriveIntentData(a.record);
    safeLocalStorageSetItem(STORAGE_KEYS.INTENTION_RECORD, JSON.stringify(a.record));
    safeLocalStorageSetItem(STORAGE_KEYS.INTENT_LABELS, JSON.stringify(intentLabels));
    if (primaryIntentTag !== null) {
      safeLocalStorageSetItem(STORAGE_KEYS.PRIMARY_INTENT_TAG, primaryIntentTag);
    } else {
      safeLocalStorageRemoveItem(STORAGE_KEYS.PRIMARY_INTENT_TAG);
    }
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENTION_PENDING);
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENTION_PENDING_ACTION);
    return {
      ...s,
      intentionRecord: a.record,
      intentionAction: null,
      intentLabels,
      primaryIntentTag,
    };
  },
  DECLINE_INTENTION: (s) => {
    const record = { kind: 'declined' as const };
    safeLocalStorageSetItem(STORAGE_KEYS.INTENTION_RECORD, JSON.stringify(record));
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENTION_PENDING);
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENTION_PENDING_ACTION);
    return {
      ...s,
      intentionRecord: record,
      intentionAction: null,
    };
  },
  CLEAR_INTENTION: (s) => {
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENTION_RECORD);
    safeLocalStorageRemoveItem(STORAGE_KEYS.INTENT_LABELS);
    safeLocalStorageRemoveItem(STORAGE_KEYS.PRIMARY_INTENT_TAG);
    return { ...s, intentionRecord: { kind: 'unset' }, intentLabels: [], primaryIntentTag: null };
  },
  SHOW_END_INTENTION: (s) => ({ ...s, showEndIntention: true }),
  DISMISS_END_INTENTION: (s) => ({ ...s, showEndIntention: false }),
  SHOW_SCHEDULE_OVERLAY: (s) => ({ ...s, showScheduleOverlay: true }),
  HIDE_SCHEDULE_OVERLAY: (s) => ({ ...s, showScheduleOverlay: false }),
  RETURNING_START:    (s) => ({ ...s, isReturning: true, otherTabActive: false }),
  RETURNING_COMPLETE: (s) => ({ ...s, isReturning: false, otherTabActive: false, otherTabRetreatName: null }),
  OTHER_TAB_ACTIVE:   (s, a) => ({ ...s, otherTabActive: true, isReturning: true, otherTabRetreatName: a.retreatName ?? null }),
  OTHER_TAB_TAKEOVER: (s) => ({ ...s, otherTabActive: false, isReturning: true }),
  [UI_ACTION_TYPES.TAKE_OWNERSHIP]: (s) => ({ ...s, otherTabActive: false, isReturning: false, otherTabRetreatName: null }),
  [UI_ACTION_TYPES.RETREAT_ENDED_GLOBALLY]: (s) => ({ ...s, otherTabActive: false, isReturning: false, otherTabRetreatName: null }),
  SETTINGS_HYDRATED: (s) => ({
    ...s,
    ...getSettingsSnapshot(),
    audioReflectionEnabled: getAudioReflectionEnabled(),
  }),
  PUSH_ALARM_REGISTRATION_RESULT: (s, a) => ({ ...s, pushAlarmUnconfirmed: !a.confirmed }),
};
